import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { API_URL } from "../../config";
import "./mindmap.scss";
import { Link } from "react-router-dom";

import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node as FlowNode,
  Edge as FlowEdge,
  applyNodeChanges,
  NodeChange,
} from "reactflow";
import "reactflow/dist/style.css";

interface Branch {
  name: string;
  subbranches?: (Branch | string)[];
}

export interface MindmapData {
  topic: string;
  branches: Branch[];
}

const COLOR_PALETTE = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#FFA07A",
  "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E2",
  "#FF8C69", "#20B2AA", "#DDA0DD", "#90EE90",
];

const NODE_V_GAP = 150;
const LEVEL_H_GAP = 350;

interface TreeNode {
  id: string;
  label: string;
  level: number;
  color: string;
  children: TreeNode[];
}

function buildMindmapLayout(data: MindmapData): { initialNodes: FlowNode[]; edges: FlowEdge[] } {
  const flowNodes: FlowNode[] = [];
  const flowEdges: FlowEdge[] = [];
  let counter = 0;

  const makeId = () => `mn_${counter++}`;

  const buildTree = (raw: any, level: number, color: string): TreeNode => {
    if (!raw) return { id: makeId(), label: "", level, color, children: [] };
    const label = typeof raw === "string" ? raw : (raw.title || raw.name || "");
    const rawSubs =
      typeof raw === "string"
        ? []
        : raw.sub_branches || raw.subbranches || raw.subBranches || [];
    const subs = Array.isArray(rawSubs) ? rawSubs : [];
    return {
      id: makeId(),
      label,
      level,
      color,
      children: subs.map((s: any) => buildTree(s, level + 1, color)),
    };
  };

  const branches = (data.branches || []).map((b, i) =>
    buildTree(b, 1, COLOR_PALETTE[i % COLOR_PALETTE.length])
  );
  const root: TreeNode = {
    id: makeId(),
    label: data.topic,
    level: 0,
    color: "#4ECDC4",
    children: branches,
  };

  const getSubtreeHeight = (node: TreeNode): number => {
    if (node.children.length === 0) return NODE_V_GAP;
    return node.children.reduce((sum, c) => sum + getSubtreeHeight(c), 0);
  };

  const assignPositions = (
    node: TreeNode,
    x: number,
    centerY: number,
    parentId?: string
  ) => {
    const isRoot = node.level === 0;
    const isL1 = node.level === 1;
    const style: React.CSSProperties = {
      background: isRoot ? "#fff" : node.color,
      color: "#000",
      border: `4px solid #000`,
      borderRadius: "0",
      padding: isRoot ? "16px 24px" : isL1 ? "12px 18px" : "8px 12px",
      minWidth: isRoot ? 200 : isL1 ? 160 : 130,
      maxWidth: isRoot ? 250 : isL1 ? 210 : 180,
      fontSize: isRoot ? "1.1rem" : isL1 ? "1rem" : "0.85rem",
      fontWeight: "900",
      fontFamily: "'Space Mono', monospace",
      textTransform: "uppercase",
      boxShadow: "6px 6px 0px #000",
      textAlign: "center",
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.3",
    };

    flowNodes.push({
      id: node.id,
      type: "default",
      data: { label: node.label, level: node.level },
      position: { x, y: centerY },
      draggable: true,
      style,
    });

    if (parentId) {
      flowEdges.push({
        id: `e_${parentId}_${node.id}`,
        source: parentId,
        target: node.id,
        type: "smoothstep",
        style: {
          stroke: "#000",
          strokeWidth: 4,
        },
        animated: false,
      });
    }

    if (node.children.length > 0) {
      const totalH = getSubtreeHeight(node);
      let curY = centerY - totalH / 2;
      for (const child of node.children) {
        const childH = getSubtreeHeight(child);
        assignPositions(child, x + LEVEL_H_GAP, curY + childH / 2, node.id);
        curY += childH;
      }
    }
  };

  assignPositions(root, 0, 0);
  return { initialNodes: flowNodes, edges: flowEdges };
}

export const MindmapFlow: React.FC<{ data: MindmapData }> = ({ data }) => {
  const saveKey = `mindmap_pos_${data.topic}`;
  const { initialNodes, edges } = useMemo(() => buildMindmapLayout(data), [data]);

  const loadWithSaved = useCallback(
    (base: FlowNode[]) => {
      try {
        const raw = localStorage.getItem(saveKey);
        if (raw) {
          const saved: Record<string, { x: number; y: number }> = JSON.parse(raw);
          return base.map((n) => ({ ...n, position: saved[n.id] ?? n.position }));
        }
      } catch {}
      return base;
    },
    [saveKey]
  );

  const [nodes, setNodes] = useState<FlowNode[]>(() => loadWithSaved(initialNodes));
  const [isDirty, setIsDirty] = useState(false);
  const [posHistory, setPosHistory] = useState<Record<string, { x: number; y: number }>[]>([]);

  // Description panel state
  const [selectedNode, setSelectedNode] = useState<{ id: string; label: string } | null>(null);
  const [nodeDesc, setNodeDesc] = useState("");
  const [descLoading, setDescLoading] = useState(false);
  const descCache = useRef(new Map<string, string>());

  // Drag tracking refs — avoids stale closures in onNodesChange
  const isDraggingRef = useRef(false);
  const preDragSnapshot = useRef<Record<string, { x: number; y: number }> | null>(null);
  const nodesRef = useRef<FlowNode[]>(nodes);
  nodesRef.current = nodes;

  useEffect(() => {
    setNodes(loadWithSaved(initialNodes));
    setIsDirty(false);
    setPosHistory([]);
    setSelectedNode(null);
    setNodeDesc("");
    descCache.current.clear();
  }, [initialNodes, loadWithSaved]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    const dragStarting =
      !isDraggingRef.current &&
      changes.some((c) => c.type === "position" && (c as any).dragging === true);
    const dragEnding = changes.some(
      (c) => c.type === "position" && (c as any).dragging === false
    );

    if (dragStarting) {
      isDraggingRef.current = true;
      const snap: Record<string, { x: number; y: number }> = {};
      nodesRef.current.forEach((n: FlowNode) => { snap[n.id] = { ...n.position }; });
      preDragSnapshot.current = snap;
    }
    if (dragEnding) {
      isDraggingRef.current = false;
      if (preDragSnapshot.current) {
        setPosHistory((prev) => [...prev.slice(-9), preDragSnapshot.current!]);
        preDragSnapshot.current = null;
      }
      setIsDirty(true);
    }
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const handleSave = useCallback(() => {
    const positions: Record<string, { x: number; y: number }> = {};
    nodesRef.current.forEach((n: FlowNode) => { positions[n.id] = n.position; });
    localStorage.setItem(saveKey, JSON.stringify(positions));
    setIsDirty(false);
  }, [saveKey]);

  const handleUndo = useCallback(() => {
    setPosHistory((prev) => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setNodes((nds) => nds.map((n) => ({ ...n, position: last[n.id] ?? n.position })));
      if (prev.length === 1) setIsDirty(false);
      return prev.slice(0, -1);
    });
  }, []);

  const onNodeClick = useCallback(
    (_: any, node: FlowNode) => {
      if (isDraggingRef.current) return;
      if ((node.data?.level ?? 0) === 0) return; // skip root

      const label = node.data?.label as string;
      setSelectedNode({ id: node.id, label });
      setNodeDesc("");

      if (descCache.current.has(node.id)) {
        setNodeDesc(descCache.current.get(node.id)!);
        return;
      }
      setDescLoading(true);
      fetch(`${API_URL}/api/mindmap/describe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: data.topic, node_label: label }),
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.status === "success") {
            descCache.current.set(node.id, res.description);
            setNodeDesc(res.description);
          }
        })
        .catch(() => {})
        .finally(() => setDescLoading(false));
    },
    [data.topic]
  );

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Action buttons */}
      <div className="mindmap-action-bar">
        {posHistory.length > 0 && (
          <button className="mindmap-undo-btn" onClick={handleUndo}>
            Undo
          </button>
        )}
        {isDirty && (
          <button className="mindmap-save-btn" onClick={handleSave}>
            Save Layout
          </button>
        )}
      </div>

      {/* Node description panel */}
      {selectedNode && (
        <div className="mindmap-desc-panel">
          <div className="mindmap-desc-header">
            <span className="mindmap-desc-title">{selectedNode.label}</span>
            <button className="mindmap-desc-close" onClick={() => setSelectedNode(null)}>
              ×
            </button>
          </div>
          <div className="mindmap-desc-body">
            {descLoading ? (
              <div className="mindmap-desc-spinner" />
            ) : (
              <p>{nodeDesc}</p>
            )}
          </div>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onNodeClick={onNodeClick}
          fitView
          nodesDraggable
          zoomOnScroll
          panOnDrag
        >
          <Background gap={24} color="#1c1c1c" />
          <Controls />
          <MiniMap
            nodeColor={(n) => {
              const border = n.style?.border as string | undefined;
              const match = border?.match(/#[0-9a-fA-F]{6}/);
              return match ? match[0] : "#444";
            }}
            style={{ background: "#0d0d0d", border: "1px solid #2a2a2a" }}
          />
        </ReactFlow>
      </div>
    </div>
  );
};

export const MindmapPage: React.FC = () => {
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState<MindmapData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topic.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/api/mindmap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Unknown error");
        return;
      }

      let raw: any = data.mindmap ?? data.raw_output ?? data;
      let mindmapData: any = raw;

      const tryParse = (s: string): any | null => {
        if (!s || typeof s !== "string") return null;
        const fenceMatch = s.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
        let candidate = fenceMatch ? fenceMatch[1] : s;
        if (/^"[\s\S]*"$/.test(candidate.trim())) {
          candidate = candidate.trim().replace(/^"|"$/g, "");
        }
        candidate = candidate.replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\'/g, "'");
        try {
          return JSON.parse(candidate);
        } catch {
          const braceMatch = candidate.match(/\{[\s\S]*\}/);
          if (braceMatch) {
            try { return JSON.parse(braceMatch[0]); } catch { return null; }
          }
          return null;
        }
      };

      if (typeof raw === "string") {
        const parsed = tryParse(raw);
        if (!parsed) { setError("Failed to parse mindmap JSON."); return; }
        mindmapData = parsed;
      }

      if (typeof mindmapData === "object" && !mindmapData.topic && mindmapData.raw_output) {
        const parsed = tryParse(mindmapData.raw_output);
        if (parsed) mindmapData = parsed;
      }

      const normalizeBranch = (b: any): Branch => {
        if (!b) return { name: "" };
        if (typeof b === "string") return { name: b };
        const title = b.title || b.name || b.label || "";
        const subs = b.sub_branches || b.subbranches || b.subBranches || b.sub || [];
        return {
          name: title,
          subbranches: Array.isArray(subs) ? subs.map(normalizeBranch) : [],
        };
      };

      const normalizedTopic =
        mindmapData.topic || mindmapData.central_topic || mindmapData.centralTopic || mindmapData.title || "";
      const rawBranches = mindmapData.branches || mindmapData.branch || mindmapData.children || [];

      if (!normalizedTopic) { setError("Parsed mindmap is missing topic."); return; }

      setResult({
        topic: normalizedTopic,
        branches: Array.isArray(rawBranches) ? rawBranches.map(normalizeBranch) : [],
      });
    } catch (err: any) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mindmap-page">
      <div className="mindmap-header">
        <Link to="/study" className="mindmap-back-link">← Back</Link>
        <h1>AI Mind Map Generator</h1>
      </div>
      <form onSubmit={handleSubmit} className="mindmap-form">
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Enter topic (e.g., Machine Learning)"
          disabled={loading}
        />
        <button type="submit" disabled={loading || !topic.trim()}>
          {loading ? "Generating…" : "Generate"}
        </button>
      </form>

      {error && <div className="mindmap-error">{error}</div>}
      {result && (
        <div className="mindmap-result">
          {result.branches && result.branches.length > 0 ? (
            <MindmapFlow data={result} />
          ) : (
            <div className="debug-info">
              <p>No branches found in mindmap data.</p>
              <pre>{JSON.stringify(result, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MindmapPage;
